import { FiltersDropdown } from '@shared/components/ui/FiltersDropdown';
import { Search } from '@shared/components/ui/Search';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReceptionFilters } from '../../models/Reception';
import { ReceptionsFiltersPanel } from './ReceptionsFiltersPanel';

interface FiltersSectionProps {
  filters: ReceptionFilters;
  onFiltersChange: (filters: ReceptionFilters) => void;
  onSearch: (term: string) => void;
  searchTerm: string;
  total?: number;
}

/**
 * Sección de filtros para recepciones.
 * Search + FiltersDropdown (desktop: dropdown, mobile: modal) con ReceptionsFiltersPanel.
 */
export const FiltersSection: React.FC<FiltersSectionProps> = ({
  filters,
  onFiltersChange,
  onSearch,
  searchTerm,
  total = 0,
}) => {
  const { t } = useTranslation('receptionsModule');
  const [inputValue, setInputValue] = useState(searchTerm);

  useEffect(() => {
    setInputValue((prev) => (prev === searchTerm ? prev : searchTerm));
  }, [searchTerm]);

  const activeFiltersCount = useMemo(() => {
    return [filters.status, filters.date_from || filters.date_to, filters.carrier].filter(Boolean).length;
  }, [filters]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleSearchSubmit = () => {
    onSearch(inputValue.trim());
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
        <Search
          placeholder={t('filter.search_placeholder')}
          value={inputValue}
          onChange={handleInputChange}
          onEnter={handleSearchSubmit}
          dataTestId="search"
          inputDataTestId="search-input"
        />

        <FiltersDropdown
          title={t('filter.filters')}
          activeFiltersCount={activeFiltersCount}
          triggerTestId="filters-toggle-button"
          dropdownContentClassName="sm:min-w-[500px] sm:max-w-[500px] p-4 bg-body-bg text-foreground border border-card-border"
        >
          <ReceptionsFiltersPanel filters={filters} onFiltersChange={onFiltersChange} />
        </FiltersDropdown>

        <div className="flex-1 text-right sm:text-right">
          <span className="text-xs sm:text-sm text-muted-foreground">
            {t('filter.total_results')}: <span className="font-medium text-foreground">{total}</span>
          </span>
        </div>
      </div>
    </div>
  );
};

export default FiltersSection;
